/**
 * API service for HTTP requests
 * Updated for simplified backend with LLM processing workflow
 */

import axios, { type AxiosInstance, AxiosError } from 'axios';
import type {
  ApiResponse,
  ApiError,
  DynamicField,
  DynamicCase,
  CreateSessionResponse,
  ApiKeyStatus,
  CaseProcessingRequest,
  CaseProcessingResponse,
  ProcessingStatus,
  CreateCaseRequest,
  UpdateCaseRequest,
  CreateFieldRequest,
  UpdateFieldRequest
} from '../types';
import type { AvatarSessionResponse } from '../types';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    // Create axios instance with base configuration
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3001',
      timeout: 30000, // Increased for LLM processing
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      }
    });

    // Request interceptor for logging
    this.client.interceptors.request.use(
      (config) => {
        console.debug(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      (error: AxiosError<ApiResponse>) => {
        const apiError: ApiError = {
          message: typeof error.response?.data?.error === 'string'
            ? error.response.data.error
            : (error.response?.data?.error as { message?: string } | undefined)?.message || error.message || 'An error occurred',
          code: error.code,
          statusCode: error.response?.status
        };

        console.error('API Response Error:', apiError);
        return Promise.reject(apiError);
      }
    );
  }

  // ================================
  // DYNAMIC FIELDS ENDPOINTS
  // ================================

  /**
   * Get all active dynamic fields
   */
  async getFields(): Promise<DynamicField[]> {
    const response = await this.client.get<ApiResponse<DynamicField[]>>('/api/fields');
    return response.data.data || [];
  }

  /**
   * Get specific field details
   */
  async getField(fieldId: string): Promise<DynamicField> {
    const response = await this.client.get<ApiResponse<DynamicField>>(`/api/fields/${fieldId}`);
    return response.data.data!;
  }

  /**
   * Create a new field (simplified - only 3 fields)
   */
  async createField(fieldData: CreateFieldRequest): Promise<DynamicField> {
    const response = await this.client.post<ApiResponse<DynamicField>>('/api/fields', fieldData);
    return response.data.data!;
  }

  /**
   * Update a field
   */
  async updateField(fieldId: string, updates: UpdateFieldRequest): Promise<DynamicField> {
    const response = await this.client.put<ApiResponse<DynamicField>>(`/api/fields/${fieldId}`, updates);
    return response.data.data!;
  }

  /**
   * Delete a field (soft delete)
   */
  async deleteField(fieldId: string): Promise<void> {
    await this.client.delete(`/api/fields/${fieldId}`);
  }

  // ================================
  // DYNAMIC CASES ENDPOINTS
  // ================================

  /**
   * Get all cases for a field
   */
  async getCases(fieldId: string): Promise<DynamicCase[]> {
    const response = await this.client.get<ApiResponse<DynamicCase[]>>('/api/cases', {
      params: { fieldId }
    });
    return response.data.data || [];
  }

  /**
   * Get a specific case
   */
  async getCase(caseId: string): Promise<DynamicCase> {
    const response = await this.client.get<ApiResponse<DynamicCase>>(`/api/cases/${caseId}`);
    return response.data.data!;
  }

  /**
   * Create a case manually (traditional method)
   */
  async createCase(caseData: CreateCaseRequest): Promise<DynamicCase> {
    const response = await this.client.post<ApiResponse<DynamicCase>>('/api/cases', caseData);
    return response.data.data!;
  }

  /**
   * Update a case
   */
  async updateCase(caseId: string, updates: UpdateCaseRequest): Promise<DynamicCase> {
    const response = await this.client.put<ApiResponse<DynamicCase>>(`/api/cases/${caseId}`, updates);
    return response.data.data!;
  }

  /**
   * Delete a case (soft delete)
   */
  async deleteCase(caseId: string): Promise<void> {
    await this.client.delete(`/api/cases/${caseId}`);
  }

  // ================================
  // LLM PROCESSING ENDPOINTS (NEW)
  // ================================

  /**
   * Process raw case text using LLM extraction
   */
  async processCase(processingData: CaseProcessingRequest): Promise<CaseProcessingResponse> {
    console.log('🤖 Starting LLM case processing:', processingData);
    
    const response = await this.client.post<ApiResponse<CaseProcessingResponse>>(
      '/api/cases/process', 
      processingData
    );
    
    console.log('🤖 LLM processing started:', response.data.data);
    return response.data.data!;
  }

  /**
   * Get processing status and progress
   */
  async getProcessingStatus(processingId: string): Promise<ProcessingStatus> {
    const response = await this.client.get<ApiResponse<ProcessingStatus>>(
      `/api/cases/processing/${processingId}`
    );
    return response.data.data!;
  }

  /**
   * Monitor processing with polling (convenience method)
   */
  async monitorProcessing(
    processingId: string, 
    onProgress?: (status: ProcessingStatus) => void,
    pollInterval: number = 2000
  ): Promise<DynamicCase> {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const status = await this.getProcessingStatus(processingId);
          
          // Call progress callback
          if (onProgress) {
            onProgress(status);
          }
          
          if (status.status === 'completed' && status.caseId) {
            // Get the completed case
            const completedCase = await this.getCase(status.caseId);
            resolve(completedCase);
          } else if (status.status === 'failed') {
            reject(new Error(status.errors?.join(', ') || 'Processing failed'));
          } else if (status.status === 'review_required') {
            reject(new Error('Processing requires manual review'));
          } else {
            // Continue polling
            setTimeout(poll, pollInterval);
          }
        } catch (error) {
          reject(error);
        }
      };
      
      poll();
    });
  }

  // ================================
  // SESSION ENDPOINTS (UPDATED)
  // ================================

  /**
   * Create a new diagnostic session
   */
  async createSession(fieldId: string, caseId: string): Promise<CreateSessionResponse> {
    const response = await this.client.post<ApiResponse<CreateSessionResponse>>('/api/sessions', {
      fieldId,
      caseId
    });
    return response.data.data!;
  }

  /**
   * Get session details
   */
  async getSession(sessionId: string): Promise<any> {
    const response = await this.client.get<ApiResponse>(`/api/sessions/${sessionId}`);
    return response.data.data;
  }

  /**
   * End a session
   */
  async endSession(sessionId: string): Promise<void> {
    await this.client.post(`/api/sessions/${sessionId}/end`);
  }

  // ================================
  // CONFIGURATION ENDPOINTS (UNCHANGED)
  // ================================

  /**
   * Get API key status
   */
  async getApiKeyStatus(): Promise<ApiKeyStatus> {
    const response = await this.client.get<ApiResponse<ApiKeyStatus>>('/api/config/api-keys');
    return response.data.data!;
  }

  /**
   * Check if the avatar provider is configured
   */
  async checkAvatarConfig(): Promise<{ configured: boolean }> {
    const response = await this.client.get<ApiResponse<{ configured: boolean }>>('/api/config/avatar-key');
    return response.data.data!;
  }

  /**
   * Create the selected avatar provider session
   */
  async createAvatarSession(params?: {
    conversationName?: string;
    provider?: "anam" | "tavus";
    caseId?: string;
  }): Promise<AvatarSessionResponse> {
    const response = await this.client.post<ApiResponse<AvatarSessionResponse>>(
      '/api/avatar/session',
      params || {}
    );
    return response.data.data!;
  }

  async avatarSessionReady(anamSessionId: string): Promise<void> {
    await this.client.post(`/api/avatar/session/${encodeURIComponent(anamSessionId)}/ready`);
  }

  /**
   * End the selected avatar provider session
   */
  async endAvatarSession(anamSessionId: string, provider: "anam" | "tavus" = "anam"): Promise<void> {
    // Keep the end request alive when navigation unloads this document.
    const response = await fetch(this.client.getUri({
      url: `/api/avatar/session/${encodeURIComponent(anamSessionId)}/end${provider === "tavus" ? "?provider=tavus" : ""}`,
    }), {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
    });
    if (!response.ok) {
      throw new Error(`Failed to end avatar session: ${response.status}`);
    }
  }

  /**
   * Get Deepgram proxy configuration
   */
  async getDeepgramConfig(): Promise<{ proxyAvailable: boolean; wsUrl: string }> {
    const response = await this.client.get<ApiResponse<{ proxyAvailable: boolean; wsUrl: string }>>('/api/config/deepgram-config');
    return response.data.data!;
  }

  /**
   * Get feature flags
   */
  async getFeatures(): Promise<Record<string, boolean>> {
    const response = await this.client.get<ApiResponse<Record<string, boolean>>>('/api/config/features');
    return response.data.data!;
  }

  // ================================
  // SEARCH AND ANALYTICS (NEW)
  // ================================

  /**
   * Search cases across fields
   */
  async searchCases(searchTerm: string, fieldId?: string): Promise<DynamicCase[]> {
    const params: any = { q: searchTerm };
    if (fieldId) params.fieldId = fieldId;
    
    const response = await this.client.get<ApiResponse<DynamicCase[]>>('/api/cases/search', { params });
    return response.data.data || [];
  }

  /**
   * Get case statistics for a field
   */
  async getCaseStatistics(fieldId?: string): Promise<any> {
    const params = fieldId ? { fieldId } : {};
    const response = await this.client.get<ApiResponse>('/api/cases/statistics', { params });
    return response.data.data;
  }

  /**
   * Get cases by processing status
   */
  async getCasesByStatus(status: DynamicCase['processing_status'], fieldId?: string): Promise<DynamicCase[]> {
    const params: any = { status };
    if (fieldId) params.fieldId = fieldId;
    
    const response = await this.client.get<ApiResponse<DynamicCase[]>>('/api/cases/by-status', { params });
    return response.data.data || [];
  }

  /**
   * Get cases needing review
   */
  async getCasesNeedingReview(fieldId?: string): Promise<DynamicCase[]> {
    return this.getCasesByStatus('review_required', fieldId);
  }

  /**
   * Get recently created cases
   */
  async getRecentCases(limit: number = 10, fieldId?: string): Promise<DynamicCase[]> {
    const params: any = { limit };
    if (fieldId) params.fieldId = fieldId;
    
    const response = await this.client.get<ApiResponse<DynamicCase[]>>('/api/cases/recent', { params });
    return response.data.data || [];
  }

  // ================================
  // BATCH OPERATIONS (OPTIONAL)
  // ================================

  /**
   * Process multiple cases in batch
   */
  async batchProcessCases(requests: CaseProcessingRequest[]): Promise<CaseProcessingResponse[]> {
    const promises = requests.map(request => this.processCase(request));
    return Promise.all(promises);
  }

  /**
   * Get multiple processing statuses
   */
  async getBatchProcessingStatus(processingIds: string[]): Promise<ProcessingStatus[]> {
    const promises = processingIds.map(id => this.getProcessingStatus(id));
    return Promise.all(promises);
  }
}

// Export singleton instance
export const api = new ApiService();
